import Vue from 'vue';
import Vuex, { StoreOptions } from 'vuex';

import { State, LoginArguments } from '@/models/store';
import Module from '@/api/module';
import Skill from '@/api/skill';
import Topic from '@/api/topic';
import School from '@/api/school';
import { BusEvent } from '@/models/bus';
import Session from '@/api/session';
import ApiList from '@/api/list';
import { bus } from '@/bus';
import Category from '@/api/category';
import Courses from '@/api/course';
import Program from './api/program';

Vue.use(Vuex);
export class Mutation {
  public static readonly LOGIN = 'LOGIN';
  public static readonly LOGOUT = 'LOGOUT';
  public static readonly CACHE_MODULE_LIST = 'CACHE_MODULE_LIST';
  public static readonly CACHE_SKILL_LIST = 'CACHE_SKILL_LIST';
  public static readonly CACHE_TOPIC_LIST = 'CACHE_TOPIC_LIST';
  public static readonly CACHE_SCHOOL_LIST = 'CACHE_SCHOOL_LIST';
  public static readonly CACHE_CATEGORY_LIST = 'CACHE_CATEGORY_LIST';
  public static readonly CACHE_COURSES_LIST = 'CACHE_COURSES_LIST';
  public static readonly CACHE_PROGRAM_LIST = 'CACHE_PROGRAM_LIST';
  public static readonly DELETE_FROM_MODULE_LIST = 'DELETE_FROM_MODULE_LIST';
  public static readonly DELETE_FROM_SKILL_LIST = 'DELETE_FROM_SKILL_LIST';
  public static readonly DELETE_FROM_TOPIC_LIST = 'DELETE_FROM_TOPIC_LIST';
  public static readonly DELETE_FROM_SCHOOL_LIST = 'DELETE_FROM_SCHOOL_LIST';
  public static readonly DELETE_FROM_CATEGORY_LIST =
    'DELETE_FROM_CATEGORY_LIST';
  public static readonly DELETE_FROM_COURSES_LIST =
    'DELETE_FROM_COURSES_LIST';
  public static readonly ADD_TO_MODULE_LIST = 'ADD_TO_MODULE_LIST';
  public static readonly ADD_TO_PROGRAM_LIST = 'ADD_TO_PROGRAM_LIST';
  public static readonly ADD_TO_SKILL_LIST = 'ADD_TO_SKILL_LIST';
  public static readonly ADD_TO_TOPIC_LIST = 'ADD_TO_TOPIC_LIST';
  public static readonly ADD_TO_SCHOOL_LIST = 'ADD_TO_SCHOOL_LIST';
  public static readonly ADD_TO_CATEGORY_LIST = 'ADD_TO_CATEGORY_LIST';
  public static readonly ADD_TO_COURSES_LIST = 'ADD_TO_COURSES_LIST';
  public static readonly MODULE_UPDATED = 'MODULE_UPDATED';
  public static readonly PROGRAM_UPDATED = 'PROGRAM_UPDATED';
  public static readonly SKILL_UPDATED = 'SKILL_UPDATED';
  public static readonly TOPIC_UPDATED = 'TOPIC_UPDATED';
  public static readonly SCHOOL_UPDATED = 'SCHOOL_UPDATED';
  public static readonly CATEGORY_UPDATED = 'CATEGORY_UPDATED';
  public static readonly COURSES_UPDATED = 'COURSES_UPDATED';
}

export class Action {
  public static readonly LOGIN = 'LOGIN';
  public static readonly LOGOUT = 'LOGOUT';
}

const storeOptions: StoreOptions<State> = {
  state: {
    sessionToken: localStorage.getItem('sessionToken') || null,
    moduleList: null,
    skillList: null,
    topicList: null,
    schoolList: null,
    categoryList: null,
    courseList: null,
    programList: null
  },

  getters: {
    isAuthenticated: (state: State): boolean => !!state.sessionToken
  },

  mutations: {
    [Mutation.LOGIN]: (state: State, sessionToken: string) => {
      state.sessionToken = sessionToken;
    },

    [Mutation.LOGOUT]: (state: State) => {
      state.sessionToken = null;

      // clear caches
      state.moduleList = null;
      state.skillList = null;
      state.topicList = null;
      state.schoolList = null;
      state.categoryList = null;
      state.courseList = null;
    },

    [Mutation.CACHE_MODULE_LIST]: (
      state: State,
      moduleList: ApiList<Module>
    ) => {
      state.moduleList = moduleList;
    },

    [Mutation.CACHE_SKILL_LIST]: (state: State, skillList: ApiList<Skill>) => {
      state.skillList = skillList;
    },

    [Mutation.CACHE_TOPIC_LIST]: (state: State, topicList: ApiList<Topic>) => {
      state.topicList = topicList;
    },

    [Mutation.CACHE_SCHOOL_LIST]: (
      state: State,
      schoolList: ApiList<School>
    ) => {
      state.schoolList = schoolList;
    },

    [Mutation.CACHE_CATEGORY_LIST]: (
      state: State,
      categoryList: ApiList<Category>
    ) => {
      state.categoryList = categoryList;
    },

    [Mutation.CACHE_COURSES_LIST]: (
      state: State,
      courseList: ApiList<Courses>
    ) => {
      state.courseList = courseList;
    },

    [Mutation.CACHE_PROGRAM_LIST]: (
      state: State,
      programList: ApiList<Program>
    ) => {
      state.programList = programList;
    },

    [Mutation.DELETE_FROM_MODULE_LIST]: async (state: State, id: number) => {
      if (state.moduleList) {
        await state.moduleList.filterById(id);
      }
    },

    [Mutation.DELETE_FROM_SKILL_LIST]: async (state: State, id: number) => {
      if (state.skillList) {
        await state.skillList.filterById(id);
      }
    },

    [Mutation.DELETE_FROM_TOPIC_LIST]: async (state: State, id: number) => {
      if (state.topicList) {
        await state.topicList.filterById(id);
      }
    },

    [Mutation.DELETE_FROM_SCHOOL_LIST]: async (state: State, id: number) => {
      if (state.schoolList) {
        await state.schoolList.filterById(id);
      }
    },

    [Mutation.DELETE_FROM_CATEGORY_LIST]: async (state: State, id: number) => {
      if (state.categoryList) {
        await state.categoryList.filterById(id);
      }
    },

    [Mutation.DELETE_FROM_COURSES_LIST]: async (state: State, id: number) => {
      if (state.courseList) {
        await state.courseList.filterById(id);
      }
    },

    [Mutation.ADD_TO_MODULE_LIST]: async (state: State, module: Module) => {
      if (state.moduleList) {
        await state.moduleList.push(module);
      }
    },

    [Mutation.ADD_TO_PROGRAM_LIST]: async (state: State, program: Program) => {
      if (state.programList) {
        await state.programList.push(program);
      }
    },

    [Mutation.ADD_TO_SKILL_LIST]: async (state: State, skill: Skill) => {
      if (state.skillList) {
        await state.skillList.push(skill);
      }
    },

    [Mutation.ADD_TO_TOPIC_LIST]: async (state: State, topic: Topic) => {
      if (state.topicList) {
        await state.topicList.push(topic);
      }
    },

    [Mutation.ADD_TO_SCHOOL_LIST]: async (state: State, school: School) => {
      if (state.schoolList) {
        await state.schoolList.push(school);
      }
    },

    [Mutation.ADD_TO_CATEGORY_LIST]: async (
      state: State,
      category: Category
    ) => {
      if (state.categoryList) {
        await state.categoryList.push(category);
      }
    },

    [Mutation.ADD_TO_COURSES_LIST]: async (
      state: State,
      courses: Courses
    ) => {
      if (state.courseList) {
        await state.courseList.push(courses);
      }
    },

    [Mutation.MODULE_UPDATED]: async (state: State, module: Module) => {
      if (state.moduleList) {
        await state.moduleList.update(module);
      }
    },

    [Mutation.PROGRAM_UPDATED]: async (state: State, program: Program) => {
      if (state.moduleList) {
        await state.programList.update(program);
      }
    },

    [Mutation.SKILL_UPDATED]: async (state: State, skill: Skill) => {
      if (state.skillList) {
        await state.skillList.update(skill);
      }
    },

    [Mutation.TOPIC_UPDATED]: async (state: State, topic: Topic) => {
      if (state.topicList) {
        await state.topicList.update(topic);
      }
    },

    [Mutation.SCHOOL_UPDATED]: async (state: State, school: School) => {
      if (state.schoolList) {
        await state.schoolList.update(school);
      }
    },

    [Mutation.CATEGORY_UPDATED]: async (state: State, category: Category) => {
      if (state.categoryList) {
        await state.categoryList.update(category);
      }
    },

    [Mutation.COURSES_UPDATED]: async (state: State, courses: Courses) => {
      if (state.courseList) {
        await state.courseList.update(courses);
      }
    }
  },

  actions: {
    async [Action.LOGIN](context, payload: LoginArguments) {
      try {
        const sessionToken = await Session.login(
          payload.username,
          payload.password
        );
        context.commit(Mutation.LOGIN, sessionToken);
        localStorage.setItem('sessionToken', sessionToken);
      } catch (e) {
        bus.$emit(BusEvent.ERROR, 'Login failed', e);
      }
    },

    async [Action.LOGOUT](context) {
      if (context.state.sessionToken === null) {
        return;
      }

      try {
        await Session.logout(context.state.sessionToken);
        context.commit(Mutation.LOGOUT);
        localStorage.removeItem('sessionToken');
      } catch (e) {
        bus.$emit(BusEvent.ERROR, 'Logout failed', e);
      }
    }
  }
};

const store = new Vuex.Store(storeOptions);
export default store;
